import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ValorVerticalTimeline.css";
import eventsJson from "../../data/events.json";

/**
 * Normalize legacy flags such as "TRUE" or true.
 */
const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return false;
};

/**
 * Vertical timeline with subtle scroll-driven glow.
 *
 * Props:
 *  - events?: array
 *  - defaultFilter?: 'all' | 'run' | 'workshop' | ...
 */
export default function ValorVerticalTimeline({
  events = [],
  defaultFilter = "all",
}) {
  // Source events (prop -> fallback -> sorted ascending by date)
  const src = useMemo(() => {
    const fallback = Array.isArray(eventsJson)
      ? eventsJson
      : eventsJson?.events ?? [];
    const base = events.length ? events : fallback;
    return base
      .slice()
      .sort((a, b) => +new Date(a.date) - +new Date(b.date));
  }, [events]);

  // "Today" at midnight (for upcoming vs past)
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Filter state
  const [filter, setFilter] = useState(() =>
    (defaultFilter || "all").toLowerCase()
  );

  // Keep filter in sync with prop (if they change it)
  useEffect(() => {
    setFilter((defaultFilter || "all").toLowerCase());
  }, [defaultFilter]);

  // Counts for filter chips
  const counts = useMemo(() => {
    const base = {
      all: src.length,
      run: 0,
      workshop: 0,
    };

    for (const e of src) {
      const t = (e.type || "").toLowerCase();
      if (t === "run") base.run += 1;
      if (t === "workshop") base.workshop += 1;
    }
    return base;
  }, [src]);

  // Filtered collection
  const filtered = useMemo(() => {
    const normalized = (filter || "all").toLowerCase();
    if (normalized === "all") return src;
    return src.filter(
      (e) => (e.type || "").toLowerCase() === normalized
    );
  }, [src, filter]);

  // Group by year
  const groups = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const y = new Date(e.date).getFullYear();
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(e);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [filtered]);

  const listRef = useRef(null);

  // IntersectionObserver + scroll progress
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const items = Array.from(container.querySelectorAll(".vt-event"));
    if (!items.length) return;

    const prefersReducedMotion = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

    let io = null;

    if (!prefersReducedMotion && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
            }
          });
        },
        {
          rootMargin: "0px 0px -15% 0px",
          threshold: 0.2,
        }
      );
      items.forEach((el) => io.observe(el));
    } else {
      // If reduced motion, just show everything
      items.forEach((el) => el.classList.add("is-visible"));
    }

    // Scroll progress -> CSS var --progress (for glow / bar)
    let raf = 0;
    const onScroll = () => {
      if (prefersReducedMotion) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const vh = window.innerHeight || 800;
        items.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const dist = Math.abs(center - vh / 2);
          const norm = Math.max(0, 1 - dist / (vh / 2)); // 0..1
          el.style.setProperty("--progress", norm.toFixed(3));
        });
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (io) io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [groups]);

  // Back-to-top button visibility
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const handle = () => setShowTop(window.scrollY > 420);
    handle();
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  return (
    <section id="events" className="vt-wrap" aria-label="Event timeline">
      <header className="vt-header">
        <div className="vt-header-main">
          <h2 className="vt-title">Upcoming &amp; Past Events</h2>
          <p className="vt-subtitle">
            Follow every major step along the journey – runs, workshops and
            more.
          </p>
        </div>

        <div
          className="vt-filters"
          role="tablist"
          aria-label="Filter events by type"
        >
          {["all", "run", "workshop"].map((key) => {
            const isActive = filter === key;
            const label =
              key === "all"
                ? "All events"
                : key.charAt(0).toUpperCase() + key.slice(1);
            const count = counts[key] ?? 0;

            return (
              <button
                key={key}
                role="tab"
                aria-selected={isActive}
                className={`vt-chip ${isActive ? "is-active" : ""}`}
                onClick={() => setFilter(key)}
                type="button"
              >
                <span className="vt-chip-label">{label}</span>
                <span className="vt-chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </header>

      <div className="vt-list" ref={listRef}>
        <div className="vt-line" aria-hidden="true" />

        {groups.length === 0 && (
          <p className="vt-empty">No events match this filter (yet).</p>
        )}

        {groups.map(([year, arr]) => (
          <div className="vt-year" key={year}>
            <div className="vt-year-sticky" aria-hidden="true">
              <span>{year}</span>
            </div>

            {arr.map((ev, i) => {
              const side = i % 2 === 0 ? "left" : "right";
              const stagger = `${(i % 6) * 120}ms`;

              const eventDate = new Date(ev.date);
              const isUpcoming = eventDate >= today;

              const showNotify = isTruthyFlag(ev.notice);
              const showDetails = isTruthyFlag(ev.details);

              // Build external links:
              // 1) Prefer `links` array if present.
              // 2) Fallback to legacy URL / URL_TAG fields.
              const links = [];

              if (Array.isArray(ev.links)) {
                for (const item of ev.links) {
                  if (!item || typeof item !== "object") continue;
                  const href = (item.href || item.url || "").trim?.() || "";
                  if (!href) continue;
                  const label =
                    item.label ||
                    item.text ||
                    item.tag ||
                    "Open link";
                  links.push({ href, label });
                }
              }

              if (!links.length) {
                const rawUrl = ev.URL || ev.url || "";
                if (typeof rawUrl === "string" && rawUrl.trim().length > 0) {
                  const urlLabel =
                    ev.URL_TAG || ev.urlTag || "Open link";
                  links.push({
                    href: rawUrl.trim(),
                    label: urlLabel,
                  });
                }
              }

              const hasCTA =
                showNotify || showDetails || links.length > 0;

              return (
                <article
                  key={ev.id || `${year}-${i}`}
                  className={`vt-event ${side} ${
                    isUpcoming ? "is-upcoming" : ""
                  }`}
                  data-id={ev.id}
                  style={{ "--stagger": stagger }}
                >
                  <div className="vt-node" aria-hidden="true">
                    <span className="vt-dot">
                      <span className="vt-pulse" />
                    </span>
                    <span className="vt-bar" />
                  </div>

                  <div className="vt-card">
                    <header className="vt-meta">
                      <div className="vt-meta-main">
                        <time
                          className="vt-date"
                          dateTime={ev.date}
                        >
                          {ev.yearOnly ? eventDate.toLocaleDateString(undefined, { year: "numeric" }) : eventDate.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                        </time>
                        {isUpcoming && (
                          <span className="vt-status">
                            Upcoming
                          </span>
                        )}
                      </div>

                      <span
                        className={`vt-type vt-${(
                          ev.type || "event"
                        ).toLowerCase()}`}
                      >
                        {ev.type || "Event"}
                      </span>
                    </header>

                    <h3 className="vt-card-title">{ev.title}</h3>
                    {ev.subtitle && (
                      <p className="vt-sub">{ev.subtitle}</p>
                    )}

                    {ev.image && (
                      <div className="vt-img" aria-hidden="true">
                        <img src={ev.image} alt="" loading="lazy" />
                      </div>
                    )}

                    {ev.description && (
                      <p className="vt-desc">{ev.description}</p>
                    )}

                    {hasCTA && (
                      <div className="vt-cta">
                        {showNotify && (
                          <button
                            className="vt-btn vt-primary"
                            type="button"
                            disabled
                          >
                            Notify me
                          </button>
                        )}

                        {showDetails && (
                          <button
                            className="vt-btn vt-ghost"
                            type="button"
                            disabled
                          >
                            Details
                          </button>
                        )}

                        {links.map((link, idx) => (
                          <a
                            key={link.href || idx}
                            className="vt-btn vt-link"
                            href={link.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ))}
      </div>

      <button
        className={`vt-top ${showTop ? "is-visible" : ""}`}
        onClick={() =>
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          })
        }
        aria-label="Back to top"
        type="button"
      >
        ↑
      </button>
    </section>
  );
}
